import React, { useEffect, useRef, Children } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import TagManager from 'react-gtm-module';

import styled from 'styled-components';

import {
  Capability,
  Text,
  Loader,
  Layout,
  useMediaQueryContext,
  Icon,
  ReadMore,
  Button,
} from '@humanspace/components';
import { Spacing, Colors } from '@humanspace/foundations';
import { mergeProjectsAndModels } from '../../utils/transformers';

const CenterGrid = styled.div`
  width: 100%;
  margin-bottom: ${Spacing.xxl};

  ${props =>
    !props.isResponsive &&
    `  overflow-y: scroll;
    flex-grow: 1;
      padding: 0px ${Spacing.l};
      max-height: 480px;`}

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const StyledText = styled(Text)`
  margin-top: ${Spacing.m};
  margin-bottom: ${Spacing.m};
`;

const LoaderWrapper = styled.div`
  height: 250px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
`;

const GridWrapper = styled.div`
  position: relative;
`;

const ArrowWrapper = styled.div`
  padding: ${Spacing.s};
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
`;

const StyledButton = styled(Button)`
  width: ${props => (props.isResponsive ? '100%' : '220px')};
`;

const StyledIcon = styled(Icon)`
  width: 24px;
  height: 24px;
  margin-right: ${Spacing.s};
`;

const ButtonWithIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CapabilityWrapper = styled.div`
  margin-bottom: ${Spacing.s};
`;

const CreateButtonsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.s};
  margin-top: ${Spacing.l};
  margin-bottom: ${Spacing.l};
  margin-left: 0;
  flex-direction: column;
`;

const InlineCreateForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  margin-top: ${Spacing.s};
  width: ${props => (props.isResponsive ? '100%' : '320px')};
`;

const NameInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 2px solid ${Colors.primary};
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 2px ${Colors.primary + '33'};
  }
`;

const CreateSubmitButton = styled.button`
  padding: 8px 16px;
  background: ${Colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: ${props => props.color || Colors.primary};
  margin-left: 8px;
  vertical-align: middle;
`;

const SubtitleText = styled.span`
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
`;


const ASSESSMENT_CONFIG = {
  human_capital: {
    color: '#F59E0B',
    badgeLabel: 'HC',
  },
  brain_health: {
    color: Colors.primary,
    badgeLabel: 'BH',
  },
  higher_ed: {
    color: '#6366F1',
    badgeLabel: 'HE',
  },
  stress: {
    color: '#E8637A',
    badgeLabel: 'Stress',
  },
  healthcare_facility: {
    color: '#0891B2',
    badgeLabel: 'HCF',
  },
};

const ConditionalWrapper = ({ conditional, isResponsive, children }) => {
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const elementsNumber = Children.count(children);
  const hasMoreThanThreeElements = elementsNumber > 3;
  const isArrowVisible = !isResponsive && hasMoreThanThreeElements;

  const handleScrollDown = () => {
    document.querySelector('.js-projects-grid').scroll({
      top: scrollPosition + 100,
      behavior: 'smooth',
    });

    setScrollPosition(scrollPosition + 100);
  };

  return (
    <GridWrapper>
      <CenterGrid className="js-projects-grid" isResponsive={isResponsive}>
        {children}
      </CenterGrid>
      {isArrowVisible && (
        <ArrowWrapper onClick={() => handleScrollDown(1)}>
          <Icon icon="arrow-down" />
        </ArrowWrapper>
      )}
    </GridWrapper>
  );
};

const tagManagerArgs = {
  dataLayer: {
    page: 'home',
  },
  dataLayerName: 'PageDataLayer',
};

const Projects = ({
  getProjects,
  getStressModels,
  createProject,
  updateProject,
  isLoading,
  projects,
  removeProject,
  removeStressModel,
  stressModels,
}) => {
  useEffect(() => {
    getProjects();
    localStorage.removeItem('newProject');
    TagManager.dataLayer(tagManagerArgs);
  }, [getProjects]);

  useEffect(() => {
    getStressModels();
  }, [getStressModels]);

  const [items, setItems] = React.useState([]);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [projectName, setProjectName] = React.useState('');
  const [renamingId, setRenamingId] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState('');
  const hasItemsCreated = items && items.length > 0;
  const inputRef = useRef(null);
  const renameInputRef = useRef(null);

  const { isMobile, isTablet } = useMediaQueryContext();
  const isResponsive = isMobile || isTablet;
  const history = useHistory();

  useEffect(() => {
    const itemsArray = mergeProjectsAndModels(projects, stressModels);
    setItems(itemsArray);
  }, [projects, stressModels]);

  useEffect(() => {
    if (showCreateForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateForm]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setShowCreateForm(false);
        setProjectName('');
        setRenamingId(null);
        setRenameValue('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleRenameSubmit = (e, itemId) => {
    e.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    if (updateProject) {
      updateProject({ name: trimmed }, itemId);
    }
    // Optimistically update local state
    setItems(prev => prev.map(it =>
      it.id === itemId ? { ...it, name: trimmed } : it
    ));
    setRenamingId(null);
    setRenameValue('');
  };

  const handleNewProjectClick = () => {
    setShowCreateForm(!showCreateForm);
    setProjectName('');
  };

  const handleCreateSubmit = e => {
    e.preventDefault();
    const trimmed = projectName.trim();
    if (!trimmed) return;
    createProject({ name: trimmed });
    setShowCreateForm(false);
    setProjectName('');
  };

  const getItemRoute = item => {
    if (item.type === 'model') return `/models/${item.id}/results`;
    return `/projects/${item.id}/hub`;
  };

  const accountId = window.localStorage.getItem('accountId');

  const getAssessmentCountLabel = item => {
    const types = item.assessmentTypes || [];
    if (types.length === 0) return 'No assessments yet';
    if (types.length === 1) return '1 assessment';
    return `${types.length} assessments`;
  };

  const getPopoverOptions = item => {
    const options = [];

    if (item.type !== 'model') {
      options.push({
        name: 'Rename',
        action: event => {
          event.preventDefault();
          setRenamingId(item.id);
          setRenameValue(item.name || '');
        },
      });
    }

    options.push({
      name: 'Delete',
      action: event => {
        const confirmMessage =
          item.type === 'model'
            ? 'Are you sure you want to delete this model?'
            : 'Are you sure you want to delete this project?';

        const response = window.confirm(confirmMessage);

        event.preventDefault();
        if (response === true) {
          if (item.type === 'model') {
            removeStressModel(item.id);
          } else {
            removeProject(item.id);
          }
        }
      },
      secondary: true,
    });

    return options;
  };

  return (
    <>
      {isLoading ? (
        <Layout
          hasNavigation
          render={
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          }
        />
      ) : (
        <Layout
          linkElement={Link}
          multiple
          leftColumn={
            <React.Fragment>
              <Text size="xl" bold lineHeight={1.1}>
                Your Projects
              </Text>
              <StyledText size="m" lineHeight={1.7}>
                <ReadMore>
                  HumanSpace evaluates how built environments impact people
                  using a computational modeling platform derived from published
                  research and data. To start, create a project, then add
                  assessments from the project hub.
                </ReadMore>
              </StyledText>
              <CreateButtonsContainer isResponsive={isResponsive}>
                <StyledButton
                  backgroundColor={Colors.primary}
                  isResponsive={isResponsive}
                  onClick={handleNewProjectClick}>
                  <ButtonWithIconWrapper>
                    <StyledIcon icon="plus" />
                    New Project
                  </ButtonWithIconWrapper>
                </StyledButton>
                {showCreateForm && (
                  <InlineCreateForm
                    isResponsive={isResponsive}
                    onSubmit={handleCreateSubmit}>
                    <NameInput
                      ref={inputRef}
                      type="text"
                      placeholder="Name your project..."
                      value={projectName}
                      onChange={e => setProjectName(e.target.value)}
                    />
                    <CreateSubmitButton
                      type="submit"
                      disabled={!projectName.trim()}>
                      Create
                    </CreateSubmitButton>
                  </InlineCreateForm>
                )}
              </CreateButtonsContainer>
            </React.Fragment>
          }
          rightColumn={
            <React.Fragment>
              <ConditionalWrapper
                conditional={hasItemsCreated}
                isResponsive={isResponsive}>
                {items &&
                  items.map(item => {
                    const types = item.assessmentTypes || [];
                    return (
                      <CapabilityWrapper key={item.id}>
                        <Capability
                          title={
                            <span>
                              {renamingId === item.id ? (
                                <InlineCreateForm
                                  isResponsive={isResponsive}
                                  onSubmit={e => handleRenameSubmit(e, item.id)}
                                  onClick={e => e.stopPropagation()}
                                  style={{ marginTop: 0, display: 'inline-flex', width: 'auto' }}
                                >
                                  <NameInput
                                    ref={renameInputRef}
                                    type="text"
                                    value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onBlur={() => { setRenamingId(null); setRenameValue(''); }}
                                  />
                                  <CreateSubmitButton
                                    type="submit"
                                    disabled={!renameValue.trim()}
                                    onMouseDown={e => e.preventDefault()}
                                  >
                                    Save
                                  </CreateSubmitButton>
                                </InlineCreateForm>
                              ) : (
                                item.name
                              )}
                              {types.map(t => {
                                const cfg = ASSESSMENT_CONFIG[t];
                                if (!cfg) return null;
                                return (
                                  <TypeBadge key={t} color={cfg.color}>
                                    {cfg.badgeLabel}
                                  </TypeBadge>
                                );
                              })}
                              <SubtitleText>
                                {getAssessmentCountLabel(item)}
                                {accountId && item.ownerName && (
                                  <span> &middot; by {item.ownerName}</span>
                                )}
                              </SubtitleText>
                            </span>
                          }
                          isExtended
                          popoverOptions={getPopoverOptions(item)}
                          mainActionColor={Colors.primary}
                          mainAction={{
                            name: 'Open',
                            action: () => history.push(getItemRoute(item)),
                          }}
                        />
                      </CapabilityWrapper>
                    );
                  })}
              </ConditionalWrapper>
            </React.Fragment>
          }
          hasNavigation
        />
      )}
    </>
  );
};

Projects.propTypes = {
  createProject: PropTypes.func.isRequired,
  updateProject: PropTypes.func,
  removeProject: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
};

Projects.defaultProps = {
  projects: [],
  stressModels: [],
};

export default Projects;
